import { FileSpreadsheet, RefreshCw, ReceiptIndianRupee } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, DataTable, SectionTitle } from "./AccountPrimitives";
import { fmtDate, inr } from "@/lib/format";

export function AccountWorkbench({ summary, candidates, loading, onRefresh, onGenerateInvoice, onOpenTab }) {
  const candidateColumns = [
    { key: "campaign_code", header: "Campaign", cellClassName: "font-semibold text-slate-900", render: (row) => row.campaign_code || row.campaign_name },
    { key: "client_name", header: "Client" },
    { key: "hoarding_count", header: "Sites", align: "right" },
    { key: "payment_due_date", header: "Due", render: (row) => fmtDate(row.payment_due_date) },
    { key: "final_amount", header: "Amount", align: "right", render: (row) => inr(row.final_amount) },
    {
      key: "action",
      header: "",
      align: "right",
      render: (row) => (
        <Button size="sm" onClick={() => onGenerateInvoice(row.campaign_id)}>
          <FileSpreadsheet size={14} /> Generate
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <Card className="p-5">
        <SectionTitle
          title="Accounting workbench"
          hint="Campaigns feed invoices and payments automatically. Use this screen to review what needs action."
          right={<Button variant="outline" onClick={onRefresh} disabled={loading}><RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh</Button>}
        />
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <button type="button" onClick={() => onOpenTab("workbench")} className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-left">
            <p className="text-xs font-bold uppercase tracking-wide text-blue-600">Ready to invoice</p>
            <p className="mt-1 font-display text-2xl font-semibold text-slate-900">{candidates.length}</p>
          </button>
          <button type="button" onClick={() => onOpenTab("invoices")} className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-left">
            <p className="text-xs font-bold uppercase tracking-wide text-amber-700">Outstanding</p>
            <p className="mt-1 font-display text-2xl font-semibold text-slate-900">{inr(summary?.total_outstanding || 0)}</p>
          </button>
          <button type="button" onClick={() => onOpenTab("payables")} className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-left">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Payables due</p>
            <p className="mt-1 font-display text-2xl font-semibold text-slate-900">{inr(summary?.payable_outstanding || 0)}</p>
          </button>
        </div>
      </Card>

      <Card className="space-y-4 p-5">
        <SectionTitle
          title="Campaigns ready for invoice"
          hint="These campaigns already have client, GST, charges, branch, and due-date data. Generate invoices without retyping."
          right={<Button variant="outline" onClick={() => onOpenTab("invoices")}><ReceiptIndianRupee size={16} /> View invoices</Button>}
        />
        <DataTable columns={candidateColumns} data={candidates} empty="No campaigns waiting for invoice." />
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <WorkflowCard title="Collect payments" text="Record payment from Campaign Detail. Accounts mirrors it into receipts and journal records." action="Receivables" onClick={() => onOpenTab("invoices")} />
        <WorkflowCard title="Pay rent and vendors" text="Use Payables for landowner rent, vendor dues, and settlements." action="Payables" onClick={() => onOpenTab("payables")} />
        <WorkflowCard title="Close and export" text="Review trust checks, lock period, then export Tally-ready files." action="Controls" onClick={() => onOpenTab("controls")} />
      </div>
    </div>
  );
}

function WorkflowCard({ title, text, action, onClick }) {
  return (
    <Card className="flex h-full min-h-[150px] flex-col p-4">
      <h3 className="font-display text-base font-semibold text-slate-900">{title}</h3>
      <p className="mt-1 text-sm leading-6 text-slate-500">{text}</p>
      <Button type="button" variant="outline" className="mt-auto w-full" onClick={onClick}>{action}</Button>
    </Card>
  );
}
